import { Children, type ReactNode, useEffect, useRef, useState } from 'react';
import { Flex, Text } from '@optiaxiom/react';

type TruncatedListProps = {
  children: ReactNode;
  lineClamp?: number;
  gap?: '0' | '2' | '4' | '6' | '8' | '10' | '12' | '16' | '20' | '24' | '32' | '40' | '48' | '64';
};

const OVERFLOW_INDICATOR_WIDTH = 24;

export function TruncatedList({ children, lineClamp = 1, gap = '6' }: TruncatedListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const prevWidthRef = useRef<number>(0);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const childArray = Children.toArray(children);
  const gapPx = parseInt(gap, 10);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const newWidth = entry.contentRect.width;
        const prevWidth = prevWidthRef.current;
        prevWidthRef.current = newWidth;
        if (prevWidth !== 0 && prevWidth !== newWidth) {
          setVisibleCount(null);
        }
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setVisibleCount(null);
  }, [children, lineClamp]);

  useEffect(() => {
    if (
      !containerRef.current ||
      !measureRef.current ||
      childArray.length === 0 ||
      visibleCount !== null
    ) {
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const itemElements = Array.from(measureRef.current.children);

    if (itemElements.length === 0) {
      setVisibleCount(childArray.length);
      return;
    }

    let currentLineWidth = 0;
    let currentLineCount = 1;
    let count = 0;

    for (let i = 0; i < itemElements.length; i++) {
      const itemWidth = (itemElements[i] as HTMLElement).offsetWidth;
      const widthWithGap = itemWidth + (currentLineWidth > 0 ? gapPx : 0);
      const hasMoreItems = i < itemElements.length - 1;
      const isLastAllowedLine = currentLineCount >= lineClamp;
      const reservedSpace =
        hasMoreItems && isLastAllowedLine ? OVERFLOW_INDICATOR_WIDTH + gapPx : 0;

      if (currentLineWidth + widthWithGap + reservedSpace <= containerWidth) {
        currentLineWidth += widthWithGap;
        count++;
      } else if (currentLineCount < lineClamp) {
        currentLineCount++;
        currentLineWidth = itemWidth;
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count || childArray.length);
  }, [childArray, visibleCount, lineClamp, gapPx]);

  if (childArray.length === 0) {
    return null;
  }

  const isMeasuring = visibleCount === null;
  const displayCount = visibleCount ?? childArray.length;
  const remainingCount = childArray.length - displayCount;

  return (
    <Flex
      ref={containerRef}
      flexDirection="row"
      gap={gap}
      flexWrap="wrap"
      overflow="hidden"
      flex="1"
      alignItems="center"
    >
      {isMeasuring && (
        <Flex
          ref={measureRef}
          flexDirection="row"
          gap={gap}
          flexWrap="wrap"
          style={{ visibility: 'hidden', position: 'absolute' }}
        >
          {childArray}
        </Flex>
      )}
      {!isMeasuring && (
        <>
          {childArray.slice(0, displayCount)}
          {remainingCount > 0 && (
            <Text fontSize="sm" color="fg.secondary">
              +{remainingCount}
            </Text>
          )}
        </>
      )}
    </Flex>
  );
}
